import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { PawPrint, Calendar, MapPin, Star, Plus, Clock, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUS_COLORS = {
  new: "bg-primary/15 text-primary",
  contacted: "bg-accent/30 text-accent-foreground",
  matched: "bg-secondary text-secondary-foreground",
  converted: "bg-primary text-primary-foreground",
  completed: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300",
  requested: "bg-accent/30 text-accent-foreground",
  scheduled: "bg-primary/15 text-primary",
};

export default function TrainerDashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [leads, setLeads] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [parents, setParents] = useState([]);
  const [openLeadId, setOpenLeadId] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");

  // Assign homework dialog
  const [hwOpen, setHwOpen] = useState(false);
  const [hwDraft, setHwDraft] = useState({
    parent_user_id: "",
    title: "",
    description: "",
    due_at: "",
  });

  const load = async () => {
    const [p, l, s, ta] = await Promise.all([
      api.get("/trainers/me/profile"),
      api.get("/leads"),
      api.get("/sessions"),
      api.get("/tasks"),
    ]);
    setProfile(p.data);
    setLeads(l.data);
    setSessions(s.data);
    setTasks(ta.data);
  };
  useEffect(() => { load(); }, []);

  // Build parent options from leads + parents endpoint
  useEffect(() => {
    api.get("/parents").then(({ data }) => setParents(data)).catch(() => {});
  }, []);

  const updateLeadStatus = async (id, status) => {
    await api.patch(`/leads/${id}`, { status });
    toast.success(`Status: ${status}`);
    await load();
  };

  const addNote = async (leadId) => {
    if (!noteDraft.trim()) return;
    await api.post("/notes", { target_type: "lead", target_id: leadId, body: noteDraft });
    setNoteDraft("");
    toast.success("Note added");
  };

  const updateSessionStatus = async (id, status) => {
    await api.patch(`/sessions/${id}`, { status });
    toast.success(`Session ${status}`);
    await load();
  };

  // Build "people I work with" from leads (parent_email → user_id lookup via parents)
  const myParents = (() => {
    const emails = new Set(leads.map((l) => l.parent_email).filter(Boolean));
    return parents.filter((p) => emails.has(p.email));
  })();

  const submitHomework = async () => {
    if (!hwDraft.parent_user_id || !hwDraft.title.trim()) {
      toast.error("Pick a family and add a title.");
      return;
    }
    try {
      await api.post("/tasks", {
        title: hwDraft.title,
        description: hwDraft.description,
        assignee_id: hwDraft.parent_user_id,
        due_at: hwDraft.due_at ? new Date(hwDraft.due_at).toISOString() : null,
      });
      toast.success("Homework assigned 📝");
      setHwOpen(false);
      setHwDraft({ parent_user_id: "", title: "", description: "", due_at: "" });
      await load();
    } catch (e) {
      toast.error("Could not assign homework");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          {profile?.avatar && <img src={profile.avatar} alt="me" className="w-14 h-14 rounded-2xl object-cover shadow-md" />}
          <div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight">{t("trainer.title")}</h1>
            <p className="text-muted-foreground text-sm">
              {user?.name} · {profile?.city}, {profile?.country}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="rounded-full bg-accent text-accent-foreground">
            <Star className="w-3 h-3 mr-1 fill-current" />{profile?.rating || "—"}
          </Badge>
          <Badge className="rounded-full bg-primary text-primary-foreground">{leads.length} leads</Badge>
        </div>
      </div>

      <Tabs defaultValue="leads">
        <TabsList className="rounded-full p-1 bg-muted overflow-x-auto flex-nowrap">
          <TabsTrigger value="leads" className="rounded-full px-5" data-testid="trainer-tab-leads">{t("trainer.tab.leads")}</TabsTrigger>
          <TabsTrigger value="sessions" className="rounded-full px-5" data-testid="trainer-tab-sessions">{t("trainer.tab.sessions")}</TabsTrigger>
          <TabsTrigger value="homework" className="rounded-full px-5" data-testid="trainer-tab-homework">Homework</TabsTrigger>
          <TabsTrigger value="profile" className="rounded-full px-5" data-testid="trainer-tab-profile">{t("trainer.tab.profile")}</TabsTrigger>
        </TabsList>

        <TabsContent value="leads" className="mt-6 grid lg:grid-cols-2 gap-6">
          <Card className="rounded-3xl border-border/60 soft-shadow">
            <CardContent className="p-0 divide-y divide-border/60">
              {leads.length === 0 && <div className="p-6 text-muted-foreground">No leads yet.</div>}
              {leads.map((l) => (
                <button
                  key={l.id}
                  onClick={() => setOpenLeadId(l.id)}
                  className={`w-full text-left p-4 hover:bg-muted/40 transition flex items-center justify-between gap-3 ${
                    openLeadId === l.id ? "bg-muted/40" : ""
                  }`}
                  data-testid={`trainer-lead-${l.id}`}
                >
                  <div>
                    <p className="font-bold">{l.parent_name} · {l.dog_name}</p>
                    <p className="text-xs text-muted-foreground">
                      <MapPin className="w-3 h-3 inline mr-1" />{l.city}, {l.country} · {l.service}
                    </p>
                  </div>
                  <Badge className={`rounded-full ${STATUS_COLORS[l.status] || ""}`}>{l.status}</Badge>
                </button>
              ))}
            </CardContent>
          </Card>

          <Card className="rounded-3xl border-border/60 soft-shadow">
            <CardContent className="p-6">
              {(() => {
                const lead = leads.find((x) => x.id === openLeadId);
                if (!lead) return <p className="text-muted-foreground">Select a lead to view details.</p>;
                return (
                  <>
                    <h3 className="font-display text-xl font-bold mb-1">{lead.parent_name} · {lead.dog_name}</h3>
                    <p className="text-xs text-muted-foreground mb-4">{lead.dog_breed} · {lead.dog_age} · {lead.dog_temperament}</p>
                    <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                      <div><span className="text-muted-foreground">Email:</span> {lead.parent_email}</div>
                      <div><span className="text-muted-foreground">Phone:</span> {lead.parent_phone}</div>
                      <div><span className="text-muted-foreground">City:</span> {lead.city}, {lead.country}</div>
                      <div><span className="text-muted-foreground">Schedule:</span> {lead.preferred_schedule}</div>
                    </div>
                    {lead.goals && <p className="text-sm bg-muted/40 rounded-xl p-3 mb-4"><b>Goals:</b> {lead.goals}</p>}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {["contacted", "matched", "converted", "completed"].map((s) => (
                        <Button
                          key={s}
                          size="sm"
                          variant={lead.status === s ? "default" : "outline"}
                          className="rounded-full"
                          onClick={() => updateLeadStatus(lead.id, s)}
                        >
                          {s}
                        </Button>
                      ))}
                    </div>
                    <div className="border-t border-border/60 pt-4">
                      <p className="text-xs font-bold uppercase tracking-widest mb-2">Add note</p>
                      <Textarea
                        rows={3}
                        value={noteDraft}
                        onChange={(e) => setNoteDraft(e.target.value)}
                        className="rounded-xl mb-2"
                        placeholder="Session insights, next steps…"
                      />
                      <Button size="sm" onClick={() => addNote(lead.id)} className="rounded-full">Save note</Button>
                    </div>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sessions" className="mt-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {sessions.length === 0 && <p className="text-muted-foreground">No sessions yet.</p>}
            {sessions.map((s) => (
              <Card key={s.id} className="rounded-3xl border-border/60 soft-shadow">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <Badge className={`rounded-full ${STATUS_COLORS[s.status] || ""}`}>{s.status}</Badge>
                    <span className="text-xs text-muted-foreground">{s.duration_min}m</span>
                  </div>
                  <p className="font-bold flex items-center gap-1.5"><Calendar className="w-4 h-4 text-primary" />
                    {new Date(s.scheduled_at).toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{s.location} · {s.type?.replace("_", " ")}</p>
                  {s.summary && <p className="text-sm mt-3 text-muted-foreground line-clamp-3">{s.summary}</p>}
                  {s.status === "requested" && (
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" className="rounded-full flex-1" onClick={() => updateSessionStatus(s.id, "scheduled")}>
                        Confirm
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full flex-1" onClick={() => updateSessionStatus(s.id, "cancelled")}>
                        Decline
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* HOMEWORK */}
        <TabsContent value="homework" className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{tasks.length} task{tasks.length === 1 ? "" : "s"}</p>
            <Button onClick={() => setHwOpen(true)} className="rounded-full" data-testid="assign-hw-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Assign homework
            </Button>
          </div>
          <Card className="rounded-3xl border-border/60 soft-shadow">
            <CardContent className="p-0 divide-y divide-border/60">
              {tasks.length === 0 ? (
                <div className="p-6 text-muted-foreground">You haven't assigned any homework yet.</div>
              ) : (
                tasks.map((tk) => (
                  <div key={tk.id} className="p-4 flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mt-0.5 shrink-0 ${
                      tk.status === "done" ? "bg-primary border-primary" : "border-muted-foreground"
                    }`}>
                      {tk.status === "done" && <CheckCircle2 className="w-4 h-4 text-primary-foreground" />}
                    </div>
                    <div className="flex-1">
                      <p className={`font-bold ${tk.status === "done" ? "line-through text-muted-foreground" : ""}`}>{tk.title}</p>
                      {tk.description && <p className="text-sm text-muted-foreground mt-0.5">{tk.description}</p>}
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        {tk.due_at && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Due {new Date(tk.due_at).toLocaleDateString()}</span>}
                        <Badge variant="outline" className="rounded-full">{tk.status}</Badge>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="profile" className="mt-6">
          <Card className="rounded-3xl border-border/60 soft-shadow">
            <CardContent className="p-6 grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">About</p>
                <p className="text-sm">{profile?.bio}</p>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Specialties</p>
                <div className="flex flex-wrap gap-2">
                  {(profile?.specialties || []).map((s) => (
                    <Badge key={s} variant="outline" className="rounded-full">{s}</Badge>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Cities</p>
                <div className="flex flex-wrap gap-2">
                  {(profile?.cities || []).map((c) => (
                    <Badge key={c} variant="outline" className="rounded-full"><MapPin className="w-3 h-3 mr-1" />{c}</Badge>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Certifications</p>
                <div className="flex flex-wrap gap-2">
                  {(profile?.certifications || []).map((c) => (
                    <Badge key={c} variant="outline" className="rounded-full">{c}</Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ASSIGN HOMEWORK DIALOG */}
      <Dialog open={hwOpen} onOpenChange={setHwOpen}>
        <DialogContent className="rounded-3xl max-w-lg" data-testid="assign-hw-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Assign homework 📝</DialogTitle>
            <DialogDescription>Help families practice between sessions.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Family</Label>
              <Select value={hwDraft.parent_user_id} onValueChange={(v) => setHwDraft({ ...hwDraft, parent_user_id: v })}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="hw-family"><SelectValue placeholder="Select a family" /></SelectTrigger>
                <SelectContent>
                  {myParents.length === 0 && <SelectItem value="__none__" disabled>No matched families yet</SelectItem>}
                  {myParents.map((p) => (
                    <SelectItem key={p.user_id} value={p.user_id}>{p.name} ({p.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title</Label>
              <Input
                value={hwDraft.title}
                onChange={(e) => setHwDraft({ ...hwDraft, title: e.target.value })}
                placeholder="Practice 'place' command 3× a day"
                className="rounded-xl mt-1"
                data-testid="hw-title"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                rows={3}
                value={hwDraft.description}
                onChange={(e) => setHwDraft({ ...hwDraft, description: e.target.value })}
                placeholder="Why it matters and how to do it"
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <Label>Due</Label>
              <Input
                type="datetime-local"
                value={hwDraft.due_at}
                onChange={(e) => setHwDraft({ ...hwDraft, due_at: e.target.value })}
                className="rounded-xl mt-1"
              />
            </div>
            <Button onClick={submitHomework} className="w-full rounded-full font-bold mt-2" data-testid="hw-submit">
              Send to family
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
